import React from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Printer, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Auth-gated, print-optimised shell for internal-only flyers.
 * Only signed-in admins can view. The flyer surface itself is A5/A4-ready and
 * the screen chrome (toolbar / instructions) is hidden when printing.
 */
export default function FlyerPage({ title, testid, accent = "var(--te-primary)", children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-zinc-100 text-black">
      {/* Block search engines */}
      <FlyerMeta title={title} />

      {/* Screen toolbar — hidden on print */}
      <div
        className="no-print sticky top-0 z-40 border-b border-zinc-300 bg-zinc-50"
        data-testid={`${testid}-toolbar`}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-black transition"
              data-testid={`${testid}-back`}
            >
              <ArrowLeft size={14} /> Back to admin
            </button>
            <span className="text-xs text-zinc-400 hidden sm:inline">·</span>
            <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider hidden sm:inline">
              Internal flyer · not indexed
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/flyers"
              className="text-xs font-medium text-zinc-500 hover:text-black transition hidden md:inline-flex items-center gap-1"
            >
              All flyers <ExternalLink size={11} />
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white"
              style={{ background: accent }}
              data-testid={`${testid}-print`}
            >
              <Printer size={14} /> Print / Save as PDF
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-3">
          <div className="text-[11px] text-zinc-500 leading-snug">
            <strong className="text-zinc-700">How to print:</strong> click
            <strong> Print / Save as PDF</strong> → in the print dialog choose
            paper <strong>A5</strong> (or A4 to print two per page),
            <strong> margins: Default</strong>, and
            <strong> background graphics: ON</strong>. Save as PDF or send
            straight to your printer.
          </div>
        </div>
      </div>

      {/* The flyer sheet */}
      <div className="max-w-5xl mx-auto px-4 py-8 print:p-0 print:max-w-none">
        <div
          className="flyer-sheet bg-black text-white shadow-2xl print:shadow-none mx-auto"
          data-testid={`${testid}-sheet`}
        >
          {children}
        </div>
      </div>

      <style>{`
        /* Screen sizing — A5 portrait approximated at 148×210mm. */
        .flyer-sheet {
          width: 148mm;
          min-height: 210mm;
          padding: 14mm 12mm;
          box-sizing: border-box;
          color: #fff;
          background: #050505;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
        }
        @media print {
          @page { size: A5 portrait; margin: 0; }
          html, body { background: #050505 !important; }
          .no-print { display: none !important; }
          .flyer-sheet {
            width: 148mm;
            min-height: 210mm;
            box-shadow: none !important;
            page-break-after: avoid;
          }
        }
        .flyer-sheet h1, .flyer-sheet h2, .flyer-sheet h3 {
          font-family: inherit;
          letter-spacing: -0.02em;
          line-height: 1.05;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

function FlyerMeta({ title }) {
  React.useEffect(() => {
    if (title) document.title = `${title} · Internal`;
    const ensure = (name) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      return el;
    };
    ensure("robots").setAttribute("content", "noindex, nofollow, noarchive");
    return () => {
      const r = document.head.querySelector('meta[name="robots"]');
      if (r) r.remove();
    };
  }, [title]);
  return null;
}
